import { useMemo, useState, type JSX } from 'react';

import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useMakerActiveWorkOrdersQuery } from '@/hooks/useLogsheetMakerQueries';
import type { MakerWorkOrderListItem } from '@/types/logsheets-maker';

/**
 * Phase 24 (v1.4) — BulkClientStep
 *
 * Step 1 of the bulk-create wizard. Fetches the maker's active Work Orders
 * via `useMakerActiveWorkOrdersQuery` (D-229), groups them client-side by
 * `clientName` (falling back to deprecated `client` for compat), and renders
 * a vertical list of tappable Cards — one per client.
 *
 * Mobile-first: full-width cards, tap targets ≥ 44 px, single column.
 */

export interface BulkClientStepProps {
  readonly onSelectClient: (clientName: string) => void;
  readonly selectedClientName?: string | null;
}

/** Convert a client name to a URL-safe slug for data-testids. */
function slug(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function getClientName(wo: MakerWorkOrderListItem): string {
  // eslint-disable-next-line @typescript-eslint/no-deprecated -- legacy `client` field fallback per Plan 22-03 D-227
  return wo.clientName ?? wo.client ?? 'Unknown';
}

interface ClientGroup {
  readonly name: string;
  readonly woCount: number;
}

export function BulkClientStep({
  onSelectClient,
  selectedClientName,
}: BulkClientStepProps): JSX.Element {
  const wosQuery = useMakerActiveWorkOrdersQuery();
  const [search, setSearch] = useState<string>('');

  const groups: readonly ClientGroup[] = useMemo(() => {
    const wos: readonly MakerWorkOrderListItem[] = wosQuery.data ?? [];
    const counts = new Map<string, number>();
    for (const wo of wos) {
      const name = getClientName(wo);
      counts.set(name, (counts.get(name) ?? 0) + 1);
    }
    const out: ClientGroup[] = [];
    for (const [name, woCount] of counts.entries()) {
      out.push({ name, woCount });
    }
    out.sort((a, b) => a.name.localeCompare(b.name));
    return out;
  }, [wosQuery.data]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (term === '') return groups;
    return groups.filter((g) => g.name.toLowerCase().includes(term));
  }, [groups, search]);

  return (
    <div className="space-y-4" data-testid="bulk-step-client">
      <div className="space-y-1">
        <Label htmlFor="bulk-client-search">Search clients</Label>
        <Input
          id="bulk-client-search"
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
          }}
          placeholder="Filter by client name…"
          data-testid="bulk-client-search"
        />
      </div>

      {wosQuery.isLoading && (
        <p className="text-sm text-muted-foreground" data-testid="bulk-client-loading">
          Loading work orders…
        </p>
      )}
      {wosQuery.isError && (
        <p className="text-sm text-destructive" data-testid="bulk-client-error">
          Failed to load work orders.
        </p>
      )}
      {!wosQuery.isLoading && !wosQuery.isError && groups.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="bulk-client-empty">
          No active work orders available.
        </p>
      )}
      {!wosQuery.isLoading && !wosQuery.isError && groups.length > 0 && filtered.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="bulk-client-no-results">
          No clients match your search.
        </p>
      )}

      <div className="space-y-3">
        {filtered.map((group) => {
          const s = slug(group.name);
          const isSelected = selectedClientName === group.name;
          return (
            <Card
              key={group.name}
              className={
                isSelected
                  ? 'border-primary ring-2 ring-primary cursor-pointer'
                  : 'cursor-pointer hover:bg-muted/40'
              }
              data-testid={`bulk-client-card-${s}`}
              onClick={() => {
                onSelectClient(group.name);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelectClient(group.name);
                }
              }}
              style={{ minHeight: '44px' }}
            >
              <CardContent className="flex items-center justify-between py-4">
                <div className="font-medium">{group.name}</div>
                <div
                  className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-semibold"
                  data-testid={`bulk-client-count-${s}`}
                >
                  {group.woCount} WO{group.woCount === 1 ? '' : 's'}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
